// src/pages/TermsOfService.jsx
import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../utils/firebase';

function TermsOfService() {
  const { salonId } = useParams();
  const [clientName, setClientName] = useState('');
  const [clientEmail, setClientEmail] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchClientData = async () => {
      if (!salonId) {
        setLoading(false);
        return;
      }
      try {
        const clientDocRef = doc(db, 'salons', salonId);
        const docSnap = await getDoc(clientDocRef);
        if (docSnap.exists()) {
          const data = docSnap.data();
          setClientName(data.name || 'Our Salon');
          setClientEmail(data.email || '');
        }
      } catch (err) {
        console.error('Error fetching client data:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchClientData();
  }, [salonId]);

  const handleBackToLogin = () => {
    window.location.href = `/BespokeBeauty/${salonId || 'demo-salon'}/customer/login`;
  };

  if (loading) {
    return (
      <div style={{ maxWidth: '800px', margin: '0 auto', padding: '40px 20px', textAlign: 'center' }}>
        <p>Loading...</p>
      </div>
    );
  }

  return (
    <div style={{
      maxWidth: '800px',
      margin: '0 auto',
      padding: '20px',
      fontFamily: 'Arial, sans-serif',
      lineHeight: '1.6',
      color: '#333'
    }}>
      <div style={{
        borderBottom: '2px solid #E64C31',
        paddingBottom: '20px',
        marginBottom: '30px'
      }}>
        <div style={{ marginBottom: '20px' }}>
          <button
            onClick={handleBackToLogin}
            style={{
              background: '#E64C31',
              color: 'white',
              border: 'none',
              padding: '10px 20px',
              borderRadius: '5px',
              cursor: 'pointer',
              fontSize: '14px'
            }}
          >
            &larr; Back to Login
          </button>
        </div>
        <h1 style={{ color: '#E64C31', margin: '0 0 10px 0' }}>Terms of Service</h1>
        <p style={{ color: '#666', margin: '0', fontSize: '14px' }}>Effective Date: December 1, 2024</p>
      </div>

      <div>
        <div style={{ marginBottom: '30px' }}>
          <h2 style={{ color: '#1A1A2E', borderBottom: '1px solid #ddd', paddingBottom: '5px' }}>1. Agreement to Terms</h2>
          <p>
            These Terms of Service ("Terms") constitute a legally binding agreement between you
            ("Customer", "you", or "your") and {clientName} ("Salon", "we", "us", or "our") regarding
            your use of the BespokeBeauty platform and services. BespokeBeauty is a software platform
            developed and operated by APT Bespoke Pte Ltd ("Platform Provider").
          </p>
          <p>
            By accessing or using our services, you acknowledge that you have read, understood,
            and agree to be bound by these Terms. If you do not agree to these Terms, you may
            not access or use our services.
          </p>
        </div>

        <div style={{ marginBottom: '30px' }}>
          <h2 style={{ color: '#1A1A2E', borderBottom: '1px solid #ddd', paddingBottom: '5px' }}>2. Platform Provider Disclaimer</h2>
          <p>
            <strong>Important Notice:</strong> APT Bespoke Pte Ltd is solely the technology provider
            of the BespokeBeauty platform. All salon services, appointments, treatments, and
            customer relationships are exclusively between you and {clientName}.
          </p>
          <p>
            APT Bespoke Pte Ltd assumes no liability whatsoever for:
          </p>
          <ul>
            <li>Quality, safety, or outcomes of salon services</li>
            <li>Appointment scheduling errors or cancellations</li>
            <li>Payment processing issues between customers and the salon</li>
            <li>Data processing errors committed or omitted by {clientName}</li>
            <li>Any disputes arising from salon services or policies</li>
          </ul>
        </div>

        <div style={{ marginBottom: '30px' }}>
          <h2 style={{ color: '#1A1A2E', borderBottom: '1px solid #ddd', paddingBottom: '5px' }}>3. Eligibility and Account Registration</h2>
          <p>
            You must be at least 15 years of age to create an account and use our services.
            By creating an account, you represent and warrant that:
          </p>
          <ul>
            <li>You are at least 15 years of age</li>
            <li>You have the legal capacity to enter into this agreement</li>
            <li>All information you provide is accurate and complete</li>
            <li>You will maintain the accuracy of your account information</li>
          </ul>
          <p>
            For customers under 18 years of age, parental or guardian consent is required
            for certain services as determined by the salon.
          </p>
        </div>

        <div style={{ marginBottom: '30px' }}>
          <h2 style={{ color: '#1A1A2E', borderBottom: '1px solid #ddd', paddingBottom: '5px' }}>4. Service Description</h2>
          <p>
            Through the BespokeBeauty platform, {clientName} provides:
          </p>
          <ul>
            <li>Online appointment booking and management</li>
            <li>Service information and pricing</li>
            <li>Customer profile management</li>
            <li>Loyalty program participation</li>
            <li>Communication regarding appointments and promotions</li>
          </ul>
          <p>
            All actual beauty and wellness services are provided directly by {clientName}
            at their physical location in Singapore.
          </p>
        </div>

        <div style={{ marginBottom: '30px' }}>
          <h2 style={{ color: '#1A1A2E', borderBottom: '1px solid #ddd', paddingBottom: '5px' }}>5. Appointment Booking and Cancellation</h2>
          <h3 style={{ color: '#5B8FA8' }}>5.1 Booking Policy</h3>
          <ul>
            <li>Appointments are subject to availability and salon confirmation</li>
            <li>You must provide accurate contact information for appointment confirmations</li>
            <li>Special requests should be noted during booking but cannot be guaranteed</li>
          </ul>

          <h3 style={{ color: '#5B8FA8' }}>5.2 Cancellation Policy</h3>
          <ul>
            <li>Appointments may be cancelled up to 24 hours before the scheduled time</li>
            <li>Late cancellations or no-shows may result in cancellation fees</li>
            <li>The salon reserves the right to cancel appointments due to unforeseen circumstances</li>
          </ul>
        </div>

        <div style={{ marginBottom: '30px' }}>
          <h2 style={{ color: '#1A1A2E', borderBottom: '1px solid #ddd', paddingBottom: '5px' }}>6. Payment Terms</h2>
          <p>
            Payment for services is processed directly between you and {clientName}.
            The BespokeBeauty platform may facilitate payment processing but does not
            handle funds directly.
          </p>
          <ul>
            <li>Prices are displayed in Singapore Dollars (SGD)</li>
            <li>Payment is due at the time of service unless otherwise arranged</li>
            <li>Refund policies are determined solely by {clientName}</li>
            <li>Disputed charges should be addressed directly with {clientName}</li>
          </ul>
        </div>

        <div style={{ marginBottom: '30px' }}>
          <h2 style={{ color: '#1A1A2E', borderBottom: '1px solid #ddd', paddingBottom: '5px' }}>7. User Responsibilities</h2>
          <p>You agree to:</p>
          <ul>
            <li>Provide accurate and complete information</li>
            <li>Arrive on time for scheduled appointments</li>
            <li>Follow salon policies and procedures</li>
            <li>Treat salon staff and other customers with respect</li>
            <li>Not use the platform for any illegal or unauthorized purposes</li>
            <li>Maintain the confidentiality of your account credentials</li>
          </ul>
        </div>

        <div style={{ marginBottom: '30px' }}>
          <h2 style={{ color: '#1A1A2E', borderBottom: '1px solid #ddd', paddingBottom: '5px' }}>8. Prohibited Uses</h2>
          <p>You may not use our platform to:</p>
          <ul>
            <li>Violate any applicable laws or regulations</li>
            <li>Impersonate another person or entity</li>
            <li>Submit false or misleading information</li>
            <li>Interfere with or disrupt the platform's operation</li>
            <li>Attempt to gain unauthorized access to other accounts</li>
            <li>Use automated tools to access the platform</li>
          </ul>
        </div>

        <div style={{ marginBottom: '30px' }}>
          <h2 style={{ color: '#1A1A2E', borderBottom: '1px solid #ddd', paddingBottom: '5px' }}>9. Communications and Marketing</h2>
          <h3 style={{ color: '#5B8FA8' }}>9.1 Email Communications</h3>
          <p>
            By creating an account, you consent to {clientName} collecting your email address
            for sending transactional and marketing emails, including:
          </p>
          <ul>
            <li>Appointment confirmations, reminders, and updates</li>
            <li>Service notifications and important account information</li>
            <li>Promotional offers, newsletters, and special campaigns</li>
            <li>Birthday month promotions and loyalty program updates</li>
          </ul>

          <h3 style={{ color: '#5B8FA8' }}>9.2 Push Notifications</h3>
          <p>
            With your device permission, we collect your Cloud Messaging token to send
            transactional and marketing push notifications through Google Firebase services, including:
          </p>
          <ul>
            <li>Real-time appointment updates and reminders</li>
            <li>Time-sensitive service notifications</li>
            <li>Special offers and limited-time promotions</li>
            <li>Loyalty program milestone achievements</li>
          </ul>

          <h3 style={{ color: '#5B8FA8' }}>9.3 Communication Rights and Preferences</h3>
          <p>
            {clientName} reserves the right to send communications to customers according
            to their stated communication preferences. You reserve the right to unsubscribe
            from marketing communications at any time through the application settings or
            unsubscribe links. {clientName} agrees to honor these unsubscription requests
            within 7 business days of such indication.
          </p>
          <p>
            <strong>Note:</strong> Transactional emails related to your appointments and
            account security cannot be unsubscribed from as they are essential for service delivery.
          </p>
        </div>

        <div style={{ marginBottom: '30px' }}>
          <h2 style={{ color: '#1A1A2E', borderBottom: '1px solid #ddd', paddingBottom: '5px' }}>10. Data Collection and Storage</h2>
          <h3 style={{ color: '#5B8FA8' }}>10.1 Personal Information for Service Delivery</h3>
          <p>
            To provide personalized salon services, we collect and store:
          </p>
          <ul>
            <li>Month of birth for birthday promotions and age-appropriate services</li>
            <li>Address postal code for location-based offers and service areas</li>
            <li>Preferred stylists to facilitate appointment booking preferences</li>
            <li>Preferred appointment days and times for scheduling convenience</li>
          </ul>

          <h3 style={{ color: '#5B8FA8' }}>10.2 Service Enhancement Information</h3>
          <p>
            To improve and personalize your experience, we store:
          </p>
          <ul>
            <li>Complete booking history and appointment patterns</li>
            <li>Rewards points balance and loyalty program participation</li>
            <li>Campaign responses and engagement preferences</li>
            <li>Interaction data with our BespokeAI system for improved recommendations</li>
            <li>Service preferences and treatment history</li>
          </ul>

          <h3 style={{ color: '#5B8FA8' }}>10.3 Data Hosting</h3>
          <p>
            All customer data is securely stored on Google Firebase Cloud infrastructure
            with servers located in Singapore, ensuring compliance with local data
            protection requirements and optimal service performance.
          </p>
        </div>

        <div style={{ marginBottom: '30px' }}>
          <h2 style={{ color: '#1A1A2E', borderBottom: '1px solid #ddd', paddingBottom: '5px' }}>11. Data and Privacy</h2>
          <p>
            Your personal data is collected and processed in accordance with our
            Privacy Policy and Singapore's Personal Data Protection Act (PDPA).
            By using our services, you consent to the collection and use of your
            information as described in our Privacy Policy.
          </p>
        </div>

        <div style={{ marginBottom: '30px' }}>
          <h2 style={{ color: '#1A1A2E', borderBottom: '1px solid #ddd', paddingBottom: '5px' }}>12. Intellectual Property</h2>
          <p>
            The BespokeBeauty platform, including its software, design, and content,
            is owned by APT Bespoke Pte Ltd and protected by intellectual property laws.
            {clientName}'s branding, service descriptions, and content are owned by {clientName}.
          </p>
        </div>

        <div style={{ marginBottom: '30px' }}>
          <h2 style={{ color: '#1A1A2E', borderBottom: '1px solid #ddd', paddingBottom: '5px' }}>13. Limitation of Liability</h2>
          <p>
            <strong>{clientName} Liability:</strong> {clientName}'s liability is limited to
            the cost of the specific service that gave rise to the claim.
          </p>
          <p>
            <strong>Platform Provider Liability:</strong> APT Bespoke Pte Ltd shall not
            be liable for any direct, indirect, incidental, special, or consequential
            damages arising from your use of the platform or salon services.
          </p>
        </div>

        <div style={{ marginBottom: '30px' }}>
          <h2 style={{ color: '#1A1A2E', borderBottom: '1px solid #ddd', paddingBottom: '5px' }}>14. Termination</h2>
          <p>
            Either party may terminate this agreement at any time. Upon termination:
          </p>
          <ul>
            <li>Your access to the platform will be suspended</li>
            <li>All pending appointments remain valid unless cancelled</li>
            <li>Your personal data will be handled according to our data retention policy</li>
            <li>These Terms will continue to apply to any outstanding obligations</li>
          </ul>
        </div>

        <div style={{ marginBottom: '30px' }}>
          <h2 style={{ color: '#1A1A2E', borderBottom: '1px solid #ddd', paddingBottom: '5px' }}>15. Governing Law and Jurisdiction</h2>
          <p>
            These Terms are governed by the laws of Singapore. Any disputes arising
            from these Terms or your use of our services will be subject to the
            exclusive jurisdiction of the Singapore courts.
          </p>
        </div>

        <div style={{ marginBottom: '30px' }}>
          <h2 style={{ color: '#1A1A2E', borderBottom: '1px solid #ddd', paddingBottom: '5px' }}>16. Changes to Terms</h2>
          <p>
            {clientName} reserves the right to update these Terms at any time.
            Changes will be effective upon posting on the platform. Your continued
            use of the services after any changes constitutes acceptance of the new Terms.
          </p>
        </div>

        <div style={{ marginBottom: '30px' }}>
          <h2 style={{ color: '#1A1A2E', borderBottom: '1px solid #ddd', paddingBottom: '5px' }}>17. Contact Information</h2>
          <p>
            For questions about these Terms of Service, please contact:
          </p>
          <div style={{
            background: '#f5f5f5',
            padding: '15px',
            borderRadius: '5px',
            margin: '15px 0'
          }}>
            <p><strong>{clientName}</strong></p>
            <p>Email: {clientEmail}</p>
          </div>

          <p style={{ fontSize: '14px', color: '#666' }}>
            For technical platform issues, contact APT Bespoke Pte Ltd at: support@bespokecrm.cloud
          </p>
        </div>

        <div style={{
          borderTop: '1px solid #ddd',
          paddingTop: '20px',
          textAlign: 'center',
          fontSize: '14px',
          color: '#666'
        }}>
          <p>
            Last updated: December 1, 2024<br/>
            Version: 1.0
          </p>
        </div>
      </div>
    </div>
  );
}

export default TermsOfService;
